import "./RegionTree.css";
import React, { useState, useEffect, useRef, useContext } from "react";
import Tree from "rc-tree";
import RegionNode from "./RegionNode";
import { XCircleblack } from "../../../../../Components/IconComponents";
import RegionRegistrationForm from "../../CreateRegion/RegionRegistrationForm";
import { findAllChildrenRegionsById, getThemeURL, gisStateCoordinates, ladakhDistrictsInGis } from "../../../../../Services/EndPoints";
import { getData, postDataWithBody } from "../../../../../Services/Services";
import Modal from "react-bootstrap/Modal";
import RMMap from "../../RMMap/RMMap";
import { ThemeContext } from "../../../../..";

const RegionTree = () => {
  const [stateId, setStateId] = useState(null);
  const [stateName, setStateName] = useState("STATE");

  const [treeData, setTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [regionTypeId, setRegionTypeId] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [actionType, setActionType] = useState("");
  const [keyVal, setKeyVal] = useState("");
  const [actionTypeHeader, setActionTypeHeader] = useState("");
  const [id, setId] = useState(null);
  const [districtId, setDistrictId] = useState("");
  const [selectedDistrictIds, setSelectedDistrictIds] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [selectedPanchayatIds, setSelectedPanchayatIds] = useState([]);
  const [selectedVillageIds, setSelectedVillageIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const regionDataMap = useRef(new Map()).current;
  const { theme } = useContext(ThemeContext);

  // ─── Fetch stateId from profile API ───────────────────────────────────────
  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const profile = await getData(getThemeURL);
        if (profile && profile.length > 0) {
          const sid = profile[0].state_id;
          const sname = profile[0].statename || "STATE";
          setStateId(sid);
          setStateName(sname);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    getUserProfile();
  }, []);

  // ─── Initialize tree once stateId is available ────────────────────────────
  useEffect(() => {
    if (!stateId) return;

    const stateKey = String(stateId);

    window.regionTreeComponent = { showModalPopup, hideModalPopup };

    regionDataMap.set(stateKey, {
      name: stateName,
      id: stateId,
      region_type_id: null,
      gis_id: null,
      level: 0
    });

    const timer = setTimeout(() => {
      setTreeData([{
        title: stateName,
        key: stateKey,
        checkable: true
      }]);
      setExpandedKeys([stateKey]);
    }, 100);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, stateName]);

  // ─── Hierarchy helpers ────────────────────────────────────────────────────
  const getHierarchyInfo = (key) => {
    let keySanitized = null;
    let level = 0;
    let isLastNode = false;

    if (key.indexOf("-") > -1) {
      const keyParts = key.split("-");
      level = keyParts.length - 1;
      if (keyParts.length === 5) isLastNode = true;
      keySanitized = keyParts[keyParts.length - 1];
    } else {
      keySanitized = key;
      level = 0;
    }

    return { keySanitized, level, isLastNode };
  };

  const generateTreeNodes = async (treeNode) => {
    const arr = [];
    const key = treeNode.props.eventKey;
    const { keySanitized, level } = getHierarchyInfo(key);

    const rgnList = await postDataWithBody(findAllChildrenRegionsById, [keySanitized], {});

    rgnList.forEach((region) => {
      const keyVal = `${key}-${region.id}`;

      regionDataMap.set(keyVal, {
        name: region.name,
        id: region.id,
        region_type_id: region.region_type_id,
        gis_id: region.gis_id,
        level: level + 1
      });

      regionDataMap.set(region.id.toString(), {
        name: region.name,
        id: region.id,
        region_type_id: region.region_type_id,
        gis_id: region.gis_id,
        level: level + 1
      });

      const newLevel = level + 1;
      const willBeLeaf = newLevel >= 2;

      arr.push({
        title: (
          <RegionNode
            regionTypeId={region.region_type_id}
            isLastNode={willBeLeaf}
            keyVal={keyVal}
            selectedId={handleSelectedId}
            id={region.id}
            text={region.name}
          />
        ),
        key: keyVal,
        isLeaf: willBeLeaf,
        checkable: true,
      });
    });

    return arr;
  };

  const setLeaf = (treeData, curKey, level) => {
    const loopLeaf = (data, lev) => {
      const l = lev - 1;
      data.forEach((item) => {
        if (
          item.key.length > curKey.length
            ? item.key.indexOf(curKey) !== 0
            : curKey.indexOf(item.key) !== 0
        ) return;
        if (item.children) {
          loopLeaf(item.children, l);
        } else if (l < 1) {
          item.isLeaf = true;
        }
      });
    };
    loopLeaf(treeData, level + 1);
  };

  const getNewTreeData = (treeData, curKey, child, level) => {
    const loop = (data) => {
      if (level < 1) return;
      data.forEach((item) => {
        if (curKey.indexOf(item.key) === 0) {
          if (item.children) loop(item.children);
          else item.children = child;
        }
      });
    };
    loop(treeData);
    setLeaf(treeData, curKey, level);
  };

  const getAllChildKeys = (treeData, parentKey) => {
    const childKeys = [];
    const findChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.key.startsWith(parentKey + '-')) {
          childKeys.push(node.key);
          if (node.children) findChildren(node.children);
        }
      });
    };
    findChildren(treeData);
    return childKeys;
  };

  const removeFromArray = (array, item) => {
    const index = array.indexOf(item);
    if (index > -1) array.splice(index, 1);
  };

  // ─── Modal handlers ───────────────────────────────────────────────────────
  const handleSelectedId = (idval, type, keyVal, regionTypeId) => {
    showModalPopup(idval, type, keyVal, regionTypeId);
  };

  const showModalPopup = (id, type, keyVal, regionTypeId) => {
    const actionTypeStr = type === "EDIT" ? "Edit Region Information" : "Create Region";
    setRegionTypeId(regionTypeId);
    setShowPopup(true);
    setActionType(type);
    setKeyVal(keyVal);
    setActionTypeHeader(actionTypeStr);
    setId(id);
  };

  const hideModalPopup = () => setShowPopup(false);

  // ─── Tree events ──────────────────────────────────────────────────────────
  const onSelect = (info) => console.log("selected", info);

  const onLoadData = (treeNode) => {
    return new Promise(async (resolve) => {
      const newTreeData = [...treeData];
      const regionList = await generateTreeNodes(treeNode);
      getNewTreeData(newTreeData, treeNode.props.eventKey, regionList, 5);
      setTreeData(newTreeData);
      resolve();
    });
  };

  // ─── Process helpers ──────────────────────────────────────────────────────
  const processChildrenRecursively = async (
    children, checkedKeys, regions, districts, blocks, panchayats, villages
  ) => {
    for (const child of children) {
      if (!checkedKeys.includes(child.key)) {
        checkedKeys.push(child.key);
        const childData = regionDataMap.get(child.key);
        if (childData) {
          regions.push({
            name: childData.name,
            gis_id: childData.gis_id,
            id: childData.id,
            region_type_id: childData.region_type_id,
            level: childData.level
          });
          if (childData.gis_id) {
            switch (childData.level) {
              case 1: districts.push(childData.gis_id); break;
              case 2: blocks.push(childData.gis_id); break;
              case 3: panchayats.push(childData.gis_id); break;
              case 4: villages.push(childData.gis_id); break;
              default: break;
            }
          }
        }
        if (child.children) {
          await processChildrenRecursively(
            child.children, checkedKeys, regions, districts, blocks, panchayats, villages
          );
        }
      }
    }
  };

  const processIndividualNode = async (
    nodeKey, regionData, checkedKeys, regions, districts, blocks, panchayats, villages, isChecking
  ) => {
    if (isChecking) {
      if (regionData.gis_id) {
        switch (regionData.level) {
          case 1: districts.push(regionData.gis_id); break;
          case 2: blocks.push(regionData.gis_id); break;
          case 3: panchayats.push(regionData.gis_id); break;
          case 4: villages.push(regionData.gis_id); break;
          default: break;
        }
      }
      regions.push({
        name: regionData.name,
        gis_id: regionData.gis_id,
        id: regionData.id,
        region_type_id: regionData.region_type_id,
        level: regionData.level
      });

      if (regionData.level < 4) {
        const allChildKeys = getAllChildKeys(treeData, nodeKey);
        for (const childKey of allChildKeys) {
          if (!checkedKeys.includes(childKey)) {
            checkedKeys.push(childKey);
            const childData = regionDataMap.get(childKey);
            if (childData) {
              regions.push({
                name: childData.name,
                gis_id: childData.gis_id,
                id: childData.id,
                region_type_id: childData.region_type_id,
                level: childData.level
              });
              if (childData.gis_id) {
                switch (childData.level) {
                  case 1: districts.push(childData.gis_id); break;
                  case 2: blocks.push(childData.gis_id); break;
                  case 3: panchayats.push(childData.gis_id); break;
                  case 4: villages.push(childData.gis_id); break;
                  default: break;
                }
              }
            }
          }
        }
      }
    } else {
      const keysToRemove = [nodeKey, ...getAllChildKeys(treeData, nodeKey)];
      for (const keyToRemove of keysToRemove) {
        const index = checkedKeys.indexOf(keyToRemove);
        if (index > -1) checkedKeys.splice(index, 1);

        const dataToRemove = regionDataMap.get(keyToRemove);
        if (dataToRemove) {
          const regionIndex = regions.findIndex(r => r.id === dataToRemove.id);
          if (regionIndex > -1) regions.splice(regionIndex, 1);

          if (dataToRemove.gis_id) {
            switch (dataToRemove.level) {
              case 1: removeFromArray(districts, dataToRemove.gis_id); break;
              case 2: removeFromArray(blocks, dataToRemove.gis_id); break;
              case 3: removeFromArray(panchayats, dataToRemove.gis_id); break;
              case 4: removeFromArray(villages, dataToRemove.gis_id); break;
              default: break;
            }
          }
        }
      }
    }
  };

  // ─── onCheck ─────────────────────────────────────────────────────────────
  const onCheck = async (checkedKeysValue, info) => {
    if (!stateId) return;
    setIsLoading(true);

    const stateKey = String(stateId);
    const node = info.node;
    const nodeKey = node.key;
    const regionData = regionDataMap.get(nodeKey);

    const currentCheckedKeys = Array.isArray(checkedKeysValue)
      ? checkedKeysValue
      : checkedKeysValue.checked || [];

    let newCheckedKeys = [...currentCheckedKeys];
    let newSelectedRegions = [...selectedRegions];
    let newSelectedDistrictIds = [...selectedDistrictIds];
    let newSelectedBlockIds = [...selectedBlockIds];
    let newSelectedPanchayatIds = [...selectedPanchayatIds];
    let newSelectedVillageIds = [...selectedVillageIds];

    try {
      if (regionData) {
        if (info.checked) {
          if (!currentCheckedKeys.includes(nodeKey)) {
            newCheckedKeys.push(nodeKey);
          }

          // ── State root node selected ──
          if (nodeKey === stateKey) {
            const stateNode = treeData.find(item => item.key === stateKey);
            if (!stateNode?.children) {
              const stateChildren = await generateTreeNodes({ props: { eventKey: stateKey } });
              const newTreeData = [...treeData];
              getNewTreeData(newTreeData, stateKey, stateChildren, 5);
              setTreeData(newTreeData);
              await processChildrenRecursively(
                stateChildren, newCheckedKeys, newSelectedRegions,
                newSelectedDistrictIds, newSelectedBlockIds,
                newSelectedPanchayatIds, newSelectedVillageIds
              );
            } else {
              await processChildrenRecursively(
                stateNode.children, newCheckedKeys, newSelectedRegions,
                newSelectedDistrictIds, newSelectedBlockIds,
                newSelectedPanchayatIds, newSelectedVillageIds
              );
            }
          } else {
            // ── Individual node selected ──
            await processIndividualNode(
              nodeKey, regionData, newCheckedKeys, newSelectedRegions,
              newSelectedDistrictIds, newSelectedBlockIds,
              newSelectedPanchayatIds, newSelectedVillageIds, true
            );

            // Auto-check state root if all districts are selected
            if (regionData.level === 1) {
              const stateChildren = treeData.find(item => item.key === stateKey)?.children || [];
              const allDistrictKeys = stateChildren.map(c => c.key);
              const allSelected =
                allDistrictKeys.length > 0 &&
                allDistrictKeys.every(k => newCheckedKeys.includes(k));
              if (allSelected && !newCheckedKeys.includes(stateKey)) {
                newCheckedKeys.push(stateKey);
              }
            }
          }
        } else {
          // ── Unchecking ──
          if (nodeKey === stateKey) {
            const getAllDescendantKeys = (nodes, parentKey) => {
              const descendants = [];
              const processNodes = (nodeList) => {
                nodeList.forEach(n => {
                  if (n.key.startsWith(parentKey + '-') || n.key === parentKey) {
                    descendants.push(n.key);
                    if (n.children) processNodes(n.children);
                  }
                });
              };
              processNodes(nodes);
              return descendants;
            };

            const stateDescendants = getAllDescendantKeys(treeData, stateKey);
            newCheckedKeys = newCheckedKeys.filter(
              k => !stateDescendants.includes(k) && k !== stateKey
            );
            newSelectedRegions = [];
            newSelectedDistrictIds = [];
            newSelectedBlockIds = [];
            newSelectedPanchayatIds = [];
            newSelectedVillageIds = [];
          } else {
            await processIndividualNode(
              nodeKey, regionData, newCheckedKeys, newSelectedRegions,
              newSelectedDistrictIds, newSelectedBlockIds,
              newSelectedPanchayatIds, newSelectedVillageIds, false
            );

            // Auto-uncheck state root if any district is unchecked
            if (regionData.level === 1 && newCheckedKeys.includes(stateKey)) {
              newCheckedKeys = newCheckedKeys.filter(k => k !== stateKey);
            }
          }
        }
      }

      setCheckedKeys(newCheckedKeys);
      setSelectedRegions(newSelectedRegions);
      setSelectedDistrictIds(newSelectedDistrictIds);
      setSelectedBlockIds(newSelectedBlockIds);
      setSelectedPanchayatIds(newSelectedPanchayatIds);
      setSelectedVillageIds(newSelectedVillageIds);
      setDistrictId(regionData?.gis_id || "");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  // Don't render tree until stateId is loaded
  if (!stateId) {
    return <div style={{ padding: 16 }}>Loading...</div>;
  }

  return (
    <div className="tree_container mt-2 d-flex srollable-hidden" style={{ height: "375pt" }}>
      <div className="col-sm-4 tree_div" style={{ backgroundColor: 'white' }}>
        <Tree
          onSelect={onSelect}
          onCheck={onCheck}
          loadData={onLoadData}
          treeData={treeData}
          checkable={true}
          checkedKeys={checkedKeys}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          checkStrictly={true}
          showLine={true}
        />
      </div>

      <div className="image_div map-sticky" style={{ overflow: "hidden" }}>
        <RMMap
          regionManagement={true}
          containerHeight="375pt"
          selectedRegions={selectedRegions}
          selectedDistrictIds={selectedDistrictIds}
          selectedBlockIds={selectedBlockIds}
          selectedPanchayatIds={selectedPanchayatIds}
          selectedVillageIds={selectedVillageIds}
          selectedDistrictId={districtId}
          isLoading={isLoading}
          incidentBlockIds={[]}
          selectedBmskids={[]}
          onLocationSelect={() => {}}
          mapData1={{ branches: [], atms: [], incidents: [], events: [] }}
          facilityCoordinates={[]}
          bmskdetails={[]}
          selectedFacilities={[]}
          drawCircle={false}
          clearCircles={false}
          drawPolygon={false}
          clearPolygons={false}
          circleRadius={0}
          clickedPoint={null}
          setClickedPoint={() => {}}
          isKilometerEnabled={false}
          searchCoordinates={null}
          incidentMarkerData={null}
          supportPointsData={[]}
          stateId={stateId}          // ✅ passed dynamically
        />
      </div>

      {showPopup && (
        <Modal
          show={showPopup}
          size="lg"
          className={
            theme === "light"
              ? "table__modal__popup-light"
              : "table__modal__popup"
          }
          centered
        >
          <Modal.Header>
            <Modal.Title>{actionTypeHeader}</Modal.Title>
            <XCircleblack
              className="cursor-pointer"
              onClick={hideModalPopup}
              width="1.5em"
              height="1.5em"
            />
          </Modal.Header>
          <Modal.Body>
            <RegionRegistrationForm
              regionTypeId={regionTypeId}
              id={id}
              keyVal={keyVal}
              actionType={actionType}
            />
          </Modal.Body>
          <Modal.Footer />
        </Modal>
      )}
    </div>
  );
};

export default RegionTree;